/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable unicorn/consistent-function-scoping */
"use client";

import { Badge, Button } from "@resume-studio/ui";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, ChevronUp } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, { useState, useEffect } from "react";

import { components } from "../types/strapi";
import { StickySidebarCTA } from "./sticky-sidebar-cta";

type SharedRichTextComponent = components["schemas"]["SharedRichTextComponent"];

type BlogPostProps = {
  post: components["schemas"]["ArticleListResponse"];
  relatedPosts:
    | {
        slug?: string;
        title?: string;
        cover?: string;
        publishedAt?: string;
      }[]
    | undefined;
};

// Category colors for visual distinction
const categoryColors: Record<string, string> = {
  "Career Advice": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "Resume Tips": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  "Job Search": "bg-amber-500/10 text-amber-600 border-amber-500/20",
  Interview: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  default: "bg-primary/10 text-primary border-primary/20",
};

function getCategoryColor(category: string) {
  return categoryColors[category] || categoryColors.default;
}

const ShareButton = ({
  label,
  onClick,
  copied,
}: {
  label: string;
  onClick: () => void;
  copied?: boolean;
}) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200 ${
      copied
        ? "bg-emerald-50 border-emerald-200 text-emerald-700"
        : "bg-white border-gray-200 text-gray-700 hover:border-primary/30 hover:text-primary"
    }`}
  >
    {copied ? "Copied!" : label}
  </motion.button>
);

function TableOfContents({
  headings,
}: {
  headings: { id: string; text: string; level: number }[];
}) {
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-100px 0px -80% 0px" },
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings]);

  // Render sidebar structure even if no headings, to hold the CTA
  return (
    <motion.nav
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="hidden lg:block ml-8 w-80 flex-shrink-0"
    >
      {headings.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200 mb-8">
          <h4 className="font-semibold text-gray-900 mb-4">Table of Contents</h4>
          <ul className="space-y-2 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
            {headings.map((heading) => (
              <li key={heading.id}>
                <a
                  href={`#${heading.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(heading.id)?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className={`block text-sm py-1 transition-all duration-200 ${
                    heading.level === 3 ? "pl-4" : ""
                  } ${
                    activeId === heading.id
                      ? "text-primary font-medium"
                      : "text-gray-600 hover:text-primary"
                  }`}
                >
                  {heading.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="sticky top-24">
        <StickySidebarCTA />
      </div>
    </motion.nav>
  );
}

function ScrollToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-8 right-8 z-50 w-12 h-12 bg-primary text-white rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
        >
          <ChevronUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

function ReadingProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const currentProgress = (window.scrollY / totalHeight) * 100;
      setProgress(currentProgress);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200 z-50">
      <motion.div
        className="h-full bg-primary"
        style={{ width: `${progress}%` }}
        transition={{ duration: 0.1 }}
      />
    </div>
  );
}

export default function BlogPost({ post, relatedPosts }: BlogPostProps) {
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const {
    title,
    publishedAt,
    cover,
    category,
    author,
    blocks = [] as SharedRichTextComponent[],
  } = post.data?.[0]?.attributes ?? {};

  const categoryName = (category?.data?.attributes?.name as string | undefined) ?? "Article";

  // Process content to add IDs and extract headings
  const { processedBlocks, headings } = React.useMemo(() => {
    const extractedHeadings: { id: string; text: string; level: number }[] = [];

    const processed = blocks.map((block) => {
      // Type guard to ensure block has a body string
      if (!("body" in block) || typeof block.body !== "string") return block;

      let body = block.body;
      // Replace h2 and h3 with versions having ids
      body = body.replace(
        /<h([23])>(.*?)<\/h\1>/g,
        (match: string, level: string, text: string) => {
          // Simple ID generation: remove special chars, lowercase, replace spaces with hyphens
          const id = text
            .toLowerCase()
            .replace(/[^\w\s-]/g, "")
            .replace(/\s+/g, "-");

          // Ensure uniqueness if needed (simple version for now)
          const uniqueId = extractedHeadings.some((h) => h.id === id)
            ? `${id}-${extractedHeadings.length}`
            : id;

          extractedHeadings.push({
            id: uniqueId,
            text: text.replace(/<[^>]*>/g, ""), // Strip HTML from TOC text
            level: parseInt(level),
          });

          return `<h${level} id="${uniqueId}">${text}</h${level}>`;
        },
      );

      return { ...block, body };
    });

    return { processedBlocks: processed, headings: extractedHeadings };
  }, [blocks]);

  // Function to calculate reading time
  const getReadingTime = (text: string) => {
    const words = text.trim().split(/\s+/).length;
    return Math.ceil(words / 200);
  };

  const firstValidBlock = blocks.find(
    (block: SharedRichTextComponent) => typeof block.body === "string",
  );
  const readingTime = firstValidBlock
    ? getReadingTime((firstValidBlock as SharedRichTextComponent).body ?? "0")
    : "0";

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const encodedTitle = encodeURIComponent(title ?? "");
  const encodedUrl = encodeURIComponent(shareUrl);

  const handleShare = (platform: string) => {
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    };

    if (platform === "copy") {
      navigator.clipboard.writeText(shareUrl);
      setShowShareTooltip(true);
      setTimeout(() => setShowShareTooltip(false), 2000);
      return;
    }

    window.open(urls[platform as keyof typeof urls], "_blank", "width=600,height=400");
  };

  return (
    <div className="min-h-screen bg-white">
      <ReadingProgress />
      <ScrollToTop />

      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <Link
              href="/blog"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
            >
              ← Back to Blog
            </Link>
          </motion.div>

          <div className="max-w-4xl">
            {/* Category Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-6"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
                <span className="h-2 w-2 rounded-full bg-primary" />
                {categoryName}
              </div>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight"
            >
              {title ?? "Untitled"}
            </motion.h1>

            {/* Meta Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-6 flex flex-wrap items-center gap-6 text-sm text-gray-600"
            >
              {author?.data?.attributes && (
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center">
                    {author.data.attributes.name?.[0] ?? "A"}
                  </div>
                  <div className="font-medium text-gray-800">{author.data.attributes.name}</div>
                </div>
              )}

              {publishedAt && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <time dateTime={publishedAt}>
                    {new Date(publishedAt).toLocaleDateString(undefined, {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </time>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span>{readingTime} min read</span>
              </div>
            </motion.div>

            {/* Share Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative mt-6 inline-flex"
            >
              <Button
                variant="outline"
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="rounded-full border-gray-200 px-5"
              >
                Share article
              </Button>

              <AnimatePresence>
                {showShareMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.98 }}
                    className="absolute top-full mt-3 left-0 bg-white rounded-2xl shadow-lg border border-gray-200 p-4 z-20"
                  >
                    <div className="grid grid-cols-2 gap-2">
                      <ShareButton label="Twitter" onClick={() => handleShare("twitter")} />
                      <ShareButton label="Facebook" onClick={() => handleShare("facebook")} />
                      <ShareButton label="LinkedIn" onClick={() => handleShare("linkedin")} />
                      <ShareButton
                        label="Copy link"
                        onClick={() => handleShare("copy")}
                        copied={showShareTooltip}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Featured Image */}
      {cover?.data?.attributes?.url && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 mb-16"
        >
          <div className="relative overflow-hidden rounded-3xl border border-gray-200 shadow-lg">
            <Image
              src={cover.data.attributes.url}
              alt={cover.data.attributes.alternativeText ?? title ?? "Article Cover"}
              width={cover.data.attributes.width ?? 1200}
              height={cover.data.attributes.height ?? 630}
              className="w-full h-auto object-cover"
              priority
            />
          </div>
        </motion.div>
      )}

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex">
          {/* Main Content */}
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex-1 max-w-4xl mx-auto min-w-0"
          >
            <div
              className="prose prose-lg prose-gray max-w-none
              prose-headings:text-gray-900 prose-headings:font-bold prose-headings:scroll-mt-24
              prose-h1:text-4xl prose-h1:mb-8
              prose-h2:text-3xl prose-h2:mb-6 prose-h2:mt-12 prose-h2:pb-4 prose-h2:border-b prose-h2:border-gray-100
              prose-h3:text-2xl prose-h3:mb-4 prose-h3:mt-8
              prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-primary prose-a:no-underline prose-a:font-medium hover:prose-a:underline
              prose-strong:text-gray-900 prose-strong:font-semibold
              prose-ul:mb-6 prose-li:mb-2 prose-li:text-gray-700
              prose-ol:mb-6
              prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-primary/5 prose-blockquote:p-6 prose-blockquote:rounded-r-2xl prose-blockquote:not-italic prose-blockquote:text-gray-700
              prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded-lg prose-code:text-sm prose-code:font-medium
              prose-pre:bg-gray-900 prose-pre:rounded-2xl prose-pre:p-6
              prose-img:rounded-2xl prose-img:shadow-lg"
            >
              {processedBlocks.length > 0 ? (
                processedBlocks.map((block, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.05 }}
                    dangerouslySetInnerHTML={{
                      __html: (block as SharedRichTextComponent).body ?? "",
                    }}
                  />
                ))
              ) : (
                <div className="text-center py-16">
                  <div className="mx-auto mb-4 h-12 w-12 rounded-2xl bg-gray-100" />
                  <p className="text-gray-500 text-lg">No content available.</p>
                </div>
              )}
            </div>

            {/* Tags / Keywords - if you have them */}
            <div className="mt-12 pt-8 border-t border-gray-100">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-gray-500">Topics:</span>
                <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                  {categoryName}
                </Badge>
              </div>
            </div>

            {/* Author Section */}
            {author?.data?.attributes && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mt-12 rounded-3xl p-8 border border-gray-200 bg-gray-50"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center text-primary text-2xl font-bold flex-shrink-0">
                    {author.data.attributes.name?.[0] ?? "A"}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-primary font-medium mb-1">Written by</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {author.data.attributes.name}
                    </h3>
                    {(author.data.attributes as any).bio && (
                      <p className="text-gray-600 leading-relaxed">
                        {String((author.data.attributes as any).bio)}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.article>

          {/* Table of Contents - Desktop Only */}
          <TableOfContents headings={headings} />
        </div>

        {/* Related Posts */}
        {relatedPosts && relatedPosts.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-24 border-t border-gray-100 pt-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Continue Reading</h2>
              <p className="text-gray-600 text-lg">
                Discover more insights and tips to advance your career
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedPosts
                .slice(-3)
                .reverse()
                .map((relatedPost, i) => (
                  <RecommendedPost key={i} post={relatedPost} index={i} />
                ))}
            </div>

            <div className="text-center mt-12">
              <Link href="/blog">
                <Button className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200">
                  View All Articles →
                </Button>
              </Link>
            </div>
          </motion.section>
        )}

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-24 rounded-3xl p-8 lg:p-12 border border-gray-200 bg-white"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 text-left">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold uppercase tracking-wide">
                <span className="h-2 w-2 rounded-full bg-primary" />
                Start building
              </div>
              <h3 className="mt-4 text-3xl font-bold text-gray-900">Ready to build your resume?</h3>
              <p className="mt-3 text-gray-600 text-lg">
                Put these insights into action and create a professional resume in minutes.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={`${process.env.NEXT_PUBLIC_APP_URL}/try`}>
                <Button className="bg-primary hover:bg-primary/90 text-white px-8 py-3 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200">
                  Try Builder Free
                </Button>
              </a>
              <Link href="/resume-samples">
                <Button
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-primary px-8 py-3 text-lg font-semibold rounded-xl transition-all duration-200"
                >
                  Browse Templates
                </Button>
              </Link>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
}

type RelatedPost = {
  slug?: string;
  title?: string;
  cover?: string;
  publishedAt?: string;
};

function RecommendedPost({ post, index }: { post: RelatedPost; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -8 }}
      className="group"
    >
      <Link
        href={`/blog/${post.slug}`}
        className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200"
      >
        <div className="relative">
          {post.cover ? (
            <Image
              src={post.cover}
              alt={post.title ?? "Related Post"}
              width={400}
              height={250}
              className="w-full aspect-[16/10] object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full aspect-[16/10] bg-gray-100 flex items-center justify-center">
              <div className="h-10 w-16 rounded-xl bg-gray-200" />
            </div>
          )}
        </div>

        <div className="p-6">
          <h3 className="font-bold text-gray-900 group-hover:text-primary transition-colors line-clamp-2 text-lg mb-3 leading-tight">
            {post.title ?? "Untitled"}
          </h3>

          {post.publishedAt && (
            <div className="flex items-center gap-2 text-gray-500">
              <Calendar className="w-4 h-4" />
              <time className="text-sm" dateTime={post.publishedAt}>
                {new Date(post.publishedAt).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </time>
            </div>
          )}

          <div className="mt-4 flex items-center text-primary font-medium text-sm">
            <span>Read More →</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
